import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useParams } from "react-router-dom";

import useUpdateFeedResource from "@fartherfinance/frontend/api/Notifications/hooks/useUpdateFeedResource";
import { FeedStatus } from "@fartherfinance/frontend/api/Notifications/Types";
import useGetTaskV3 from "@fartherfinance/frontend/api/Tasks/hooks/useGetTask.v3";
import useGetTaskActivityLog from "@fartherfinance/frontend/api/Tasks/hooks/useGetTaskActivityLog";
import useGetTaskComments from "@fartherfinance/frontend/api/Tasks/hooks/useGetTaskComments";
import useGetTask from "@fartherfinance/frontend/api/TaskWorkflows/hooks/useGetTask";
import { TaskId } from "@fartherfinance/frontend/api/Types";

import { TASK_COMMENTS_STATIC_QUERY_PARAMS } from "../../Tasks/common";
import { useTasksNavigation } from "../../Tasks/hooks";
import TaskDrawerFooter from "../../Tasks/TaskDrawerFooter/TaskDrawerFooter";
import TaskActionButtons from "../components/TaskActionButtons";
import { FormSubmissionProvider } from "../FormEngine";
import { useFormSubmissionContext } from "../FormEngine/context";
import TaskMessageModal from "../TaskMessageModal/TaskMessageModal";
import Drawer from "@src/multiCustodian/components/Drawer/Drawer";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import Text from "@src/sharedComponents/Typography/Text";

import TaskDetailsWorkflowUI from "./TaskDetailsWorkflowUI";

import styles from "./TaskDetailsWorkflow.module.css";

interface TaskDetailsWorkflowStandaloneProps {
  disableDrawerTransition?: boolean;
}

const TaskDetailsWorkflowStandaloneInner: React.FC<
  TaskDetailsWorkflowStandaloneProps
> = ({ disableDrawerTransition: _disableDrawerTransition = false }) => {
  const auth = useAdvisorRequestAuth();

  if (!auth) {
    throw new Error(
      "Authentication required for TaskDetailsWorkflowStandalone - TaskWorkflows"
    );
  }

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [shouldCloseDrawer, setShouldCloseDrawer] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);

  const { taskId } = useParams<Record<"taskId", TaskId>>();
  const { isDirty } = useFormSubmissionContext();

  console.log("isDirty", isDirty);

  const { handleNavigateToTasksList } = useTasksNavigation();
  const callUpdateFeedResource = useUpdateFeedResource(auth);
  const [_isMutating, setIsMutating] = useState(false);

  // Fetch task data using both APIs
  // TaskWorkflows API for FormEngine
  const {
    data: taskWorkflowData,
    isLoading: isTaskWorkflowLoading,
    hasError: taskWorkflowHasError,
  } = useGetTask(taskId, auth);
  // Legacy API for existing components (TaskActivity, TaskAttachments)
  const {
    data: taskDetailsV3,
    isLoading: isTaskV3Loading,
    hasError: taskV3HasError,
  } = useGetTaskV3(taskId, auth);
  const { data: taskActivities } = useGetTaskActivityLog(taskId, auth);
  const {
    data: taskComments,
    isLoading: isTaskCommentsLoading,
    hasError: taskCommentsHasError,
  } = useGetTaskComments(taskId, TASK_COMMENTS_STATIC_QUERY_PARAMS, auth);

  const taskCommentsArray = useMemo(
    () => taskComments?.values || [],
    [taskComments?.values]
  );

  const taskAttachmentsArray = useMemo(
    () => taskWorkflowData?.attachments || [],
    [taskWorkflowData?.attachments]
  );

  const handleConfirmClose = (): void => {
    setIsConfirmModalOpen(false);
    setShouldCloseDrawer(true);
  };

  const handleCancelClose = (): void => {
    setIsConfirmModalOpen(false);
  };

  const handleDrawerClosed = (): void => {
    setShouldCloseDrawer(false);
  };

  const handleCloseDrawer = useCallback((): void => {
    if (isDirty) {
      setIsConfirmModalOpen(true);
    } else {
      setIsDrawerOpen(false);
    }
  }, [isDirty]);

  useEffect(() => {
    if (shouldCloseDrawer) {
      setIsDrawerOpen(false);
      handleDrawerClosed();
    }
  }, [shouldCloseDrawer]);

  useEffect(() => {
    if (!isDrawerOpen) {
      handleNavigateToTasksList();
    }
  }, [isDrawerOpen, handleNavigateToTasksList]);

  useEffect(() => {
    const updateFeedResource = async (): Promise<void> => {
      const personId = auth?.advisorId;

      if (personId && taskId) {
        try {
          await callUpdateFeedResource({
            resourceId: taskId,
            resourceType: "task",
            personId,
            request: { status: FeedStatus.enum.READ },
          });
        } catch {
          // NOTE: fail silently, user should not be aware of this call
        }
      }
    };

    updateFeedResource();

    // NOTE: we just want to run this effect once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLoading =
    isTaskWorkflowLoading ||
    isTaskV3Loading ||
    isTaskCommentsLoading ||
    !taskWorkflowData;

  const hasError =
    taskWorkflowHasError || taskV3HasError || taskCommentsHasError;

  const footer = useMemo(() => {
    if (!taskId || isLoading) {
      return undefined;
    }

    return isDirty ? (
      <TaskActionButtons onCancel={handleCloseDrawer} />
    ) : (
      <TaskDrawerFooter taskId={taskId} />
    );
  }, [taskId, isDirty, handleCloseDrawer, isLoading]);

  return (
    <Drawer
      isDrawerOpen={isDrawerOpen}
      onClose={handleCloseDrawer}
      footer={footer}
    >
      {isLoading && (
        <div className={styles.loading}>
          <LogoLoadingStill />
        </div>
      )}

      {hasError && (
        <div className={styles.error}>
          <Text variant="body">Error loading task details</Text>
        </div>
      )}

      {taskWorkflowData && taskDetailsV3 && !isLoading && !hasError && (
        <TaskDetailsWorkflowUI
          taskWorkflowData={taskWorkflowData}
          taskDetailsV3={taskDetailsV3}
          taskAttachments={taskAttachmentsArray}
          taskComments={taskCommentsArray}
          taskActivities={taskActivities}
          setIsDrawerOpen={setIsDrawerOpen}
          setIsMutating={setIsMutating}
          isEmbedded={false}
        />
      )}

      <TaskMessageModal
        title="Are you sure you want to leave without saving?"
        isOpen={isConfirmModalOpen}
        onClose={{
          label: "Stay",
          action: handleCancelClose,
        }}
        onConfirm={{
          label: "Leave",
          action: handleConfirmClose,
        }}
      >
        <Text variant="body" color="textSubtle">
          Your changes will not be saved.
        </Text>
      </TaskMessageModal>
    </Drawer>
  );
};

const TaskDetailsWorkflowStandalone: React.FC<
  TaskDetailsWorkflowStandaloneProps
> = (props) => {
  return (
    <FormSubmissionProvider>
      <TaskDetailsWorkflowStandaloneInner {...props} />
    </FormSubmissionProvider>
  );
};

export default TaskDetailsWorkflowStandalone;
